import { ICookieWriterOptions } from "../../types/gdpr/cookie-writer-options";

export class CookieWriterElementsProcessor {

    private readonly cookieWriterOptions: ICookieWriterOptions;
    private readonly root: HTMLElement;

    constructor(cookieWriterOptions: ICookieWriterOptions, root?: HTMLElement) {
        this.cookieWriterOptions = cookieWriterOptions;

        if (!this.cookieWriterOptions) {
            throw new Error(`${this.constructor.name} is not configured`);
        }

        this.root = root ?? document.documentElement;
    }

    public enableCookieWriters(): void {
        const possibleCookieWriterElements = this.getPossibleCookieWriters();
        if (!possibleCookieWriterElements) {
            return;
        }

        for (let i = 0; i < possibleCookieWriterElements.length; i++) {
            if (!possibleCookieWriterElements[i].hasAttribute(Constants.COOKIE_WRITERS_DATA_ATTRIBUTE)) {
                continue;
            }

            const processor = ProcessorFactory.create(this.cookieWriterOptions, possibleCookieWriterElements[i]);
            processor.enableElement();
        }
    }

    public disableCookieWriters(): void {
        const possibleCookieWriterElements = this.getPossibleCookieWriters();
        if (!possibleCookieWriterElements) {
            return;
        }

        for (let i = 0; i < possibleCookieWriterElements.length; i++) {
            if (possibleCookieWriterElements[i].hasAttribute(Constants.COOKIE_WRITERS_DATA_ATTRIBUTE)) {
                continue;
            }

            const processor = ProcessorFactory.create(this.cookieWriterOptions, possibleCookieWriterElements[i]);
            processor.disableElement();
        }
    }

    private getPossibleCookieWriters(): Array<HTMLElement> {
        const result = new Array<HTMLElement>();
        if (this.root.matches(Constants.POSSIBLE_COOKIE_WRITERS_SELECTOR)) {
            result.push(this.root);
        }

        return result.concat(Array.from(this.root.querySelectorAll<HTMLElement>(Constants.POSSIBLE_COOKIE_WRITERS_SELECTOR)));
    }

}

class ProcessorFactory {

    public static create(cookieWriterOptions: ICookieWriterOptions, element: HTMLElement): BaseProcessor {
        const tagName = element.tagName.toLowerCase();
        switch (tagName) {
            case "iframe":
                return new IframeProcessor(cookieWriterOptions, element as HTMLIFrameElement);
            case "script":
                return new ScriptProcessor(cookieWriterOptions, element as HTMLScriptElement);
            default:
                throw new Error(`There is no processor for ${tagName}`);
        }
    }

}

class BaseProcessor {

    protected readonly cookieWriterOptions: ICookieWriterOptions;
    protected readonly element: HTMLScriptElement | HTMLIFrameElement;

    constructor(cookieWriterOptions: ICookieWriterOptions, element: HTMLScriptElement | HTMLIFrameElement) {
        this.cookieWriterOptions = cookieWriterOptions;
        this.element = element;

        if (!this.cookieWriterOptions) {
            throw new Error(`${this.constructor.name} is not configured`);
        }
    }

    public disableElement(): boolean {
        return false;
    }

    public enableElement(): boolean {
        return false;
    }

    protected isRefersToEssentialHost(): boolean {
        const srcAttributeValue = this.element.getAttribute("src") ?? this.element.src;
        if (!srcAttributeValue) {
            return false;
        }

        try {
            const src = new URL(srcAttributeValue);
            const srcHost = src.hostname.replace("www.", "");
            return this.cookieWriterOptions.essentialHosts?.some(x => x === srcHost) ?? false;
        } catch {
            return false;
        }
    }

    protected isPartOfWidget(widgetNames: string[]): boolean {
        if (!widgetNames) {
            return false;
        }

        let selector = "";
        widgetNames.forEach(name => {
            selector += `[data-widget="${name}"],`;
        });

        if (selector) {
            selector = selector.slice(0, -1);
        }

        return !!this.element.closest(selector);
    }

    protected isPartOfAnyWidgetExcept(widgetNames: string[]): boolean {
        let selector = "[data-widget]";
        widgetNames.forEach(name => {
            selector += `:not([data-widget="${name}"])`;
        });

        return !!this.element.closest(selector);
    }

    protected wrapAttributeValue(dataAttribute: string, attribute: string, defaultValue: string | null = null): void {
        this.element.setAttribute(dataAttribute, this.element.getAttribute(attribute) ?? "");
        if (defaultValue) {
            this.element.setAttribute(attribute, defaultValue);
        } else {
            this.element.removeAttribute(attribute);
        }
    }

    protected unwrapAttributeValue(dataAttribute: string, attribute: string): void {
        const dataAttributeValue = this.element.getAttribute(dataAttribute);
        if (dataAttributeValue) {
            this.element.setAttribute(attribute, dataAttributeValue);
        } else {
            this.element.removeAttribute(attribute);
        }

        this.element.removeAttribute(dataAttribute);
    }

}

class IframeProcessor extends BaseProcessor {

    public override disableElement(): boolean {
        if (this.isEssential()) {
            return false;
        }

        this.element.setAttribute(Constants.COOKIE_WRITERS_DATA_ATTRIBUTE, "");

        this.wrapAttributeValue(Constants.SRC_DATA_ATTRIBUTE, "src");
        this.wrapAttributeValue(Constants.SRCDOC_DATA_ATTRIBUTE, "srcdoc");
        this.wrapAttributeValue(Constants.WIDTH_DATA_ATTRIBUTE, "width", "0");
        this.wrapAttributeValue(Constants.HEIGHT_DATA_ATTRIBUTE, "height", "0");

        return true;
    }

    private isEssential(): boolean {
        return this.isRefersToEssentialHost() ||
            this.isPartOfWidget(this.cookieWriterOptions.essentialWidgetNames);
    }

    public override enableElement(): boolean {
        this.element.removeAttribute(Constants.COOKIE_WRITERS_DATA_ATTRIBUTE);

        this.unwrapAttributeValue(Constants.SRC_DATA_ATTRIBUTE, "src");
        this.unwrapAttributeValue(Constants.SRCDOC_DATA_ATTRIBUTE, "srcdoc");
        this.unwrapAttributeValue(Constants.WIDTH_DATA_ATTRIBUTE, "width");
        this.unwrapAttributeValue(Constants.HEIGHT_DATA_ATTRIBUTE, "height");

        return true;
    }

}

class ScriptProcessor extends BaseProcessor {

    public override disableElement(): boolean {
        if (this.isEssential()) {
            return false;
        }

        this.element.setAttribute(Constants.COOKIE_WRITERS_DATA_ATTRIBUTE, "");

        this.wrapAttributeValue(Constants.TYPE_DATA_ATTRIBUTE, "type", "text/plain");
        this.wrapAttributeValue(Constants.SRC_DATA_ATTRIBUTE, "src");

        return true;
    }

    private isEssential(): boolean {
        return this.hasAttributes(this.cookieWriterOptions.essentialAttributes) ||
            this.isRefersToEssentialHost() ||
            this.isPartOfAnyWidgetExcept(this.cookieWriterOptions.nonEssentialWidgetNames);
    }

    private hasAttributes(attributes: string[]): boolean {
        if (!attributes) {
            return false;
        }

        for (let i = 0; i < this.element.attributes.length; i++) {
            const attr = this.element.attributes.item(i);
            if (attributes.includes(attr?.name ?? "")) {
                return true;
            }
        }

        return false;
    }

    public override enableElement(): boolean {
        this.element.removeAttribute(Constants.COOKIE_WRITERS_DATA_ATTRIBUTE);

        this.unwrapAttributeValue(Constants.TYPE_DATA_ATTRIBUTE, "type");
        this.unwrapAttributeValue(Constants.SRC_DATA_ATTRIBUTE, "src");

        const elementCopy = this.element.cloneNode(true) as HTMLElement;
        this.element.insertAdjacentElement("afterend", elementCopy);
        this.element.remove();

        return true;
    }

}

class Constants {

    public static readonly POSSIBLE_COOKIE_WRITERS_SELECTOR = "script, iframe";
    public static readonly COOKIE_WRITERS_DATA_ATTRIBUTE = "data-non-essential-cookie-writer";
    public static readonly SRC_DATA_ATTRIBUTE = "data-src";
    public static readonly SRCDOC_DATA_ATTRIBUTE = "data-srcdoc";
    public static readonly TYPE_DATA_ATTRIBUTE = "data-type";
    public static readonly WIDTH_DATA_ATTRIBUTE = "data-width";
    public static readonly HEIGHT_DATA_ATTRIBUTE = "data-height";

}
